/**
 * Ad hoc items — the end-of-survey checklist (spec §2.5, Q33–35).
 *
 * These are not catalog questions and deliberately have no ids in §3: they
 * are job-level extras the office quantifies from the schedule, not room facts
 * the estimate keys off. So they live on `SurveyDocument.adHoc` as structure,
 * the way rooms and cars do, and this module is the only place that shape is
 * written.
 *
 *   Storage rental                flag        the office sets the duration
 *   Dumpster rental               flag        the office sets the duration
 *   Clean pit of oil & debris     QUANTITY    = number of dirty pits; catalog prices per pit
 *   Parking                       flag        the office sets the duration
 *
 * A fifth member, `other` — free text + photos, priced by hand — is on the type
 * and the schema still, but is no longer written here (task 7, controller
 * ruling 7): the design authority called its photo picker leftover code and
 * asked for the checklist's four items, a single notes field, and the section's
 * own carousel instead. `other` stays only so an OLD record still parses and
 * its items still travel to the office; `emptySurveyDocument` still seeds it
 * `[]`.
 *
 * The one that carries a number is the one that has a per-unit price, and the
 * unit is a pit — not a room, not a car. `pitCount()` is the structural ceiling
 * for it: one pit per hoistway, across every machine room on the survey. That is
 * a fact about the entity tree, not a derived quantity, which is why it is
 * counted here and not asked of the calc engine (specification §3.6/§3.8 have no
 * AUTO lines at hoistway level — see the note in calc.ts).
 *
 * Pure: documents in, documents mutated in place by the caller's transaction.
 */

import type { SurveyDocument } from '../../core/types';

/** The three plain flags, in the mockup's order. */
export type AdHocFlagKey = 'storageRental' | 'dumpsterRental' | 'parking';

export interface AdHocFlagSpec {
  key: AdHocFlagKey;
  label: string;
}

/* The note each row carried about who sets the duration is gone (D-033 R8):
 * the office's role is not restated on the row it prices. */
export const AD_HOC_FLAGS: readonly AdHocFlagSpec[] = [
  { key: 'storageRental', label: 'Storage rental' },
  { key: 'dumpsterRental', label: 'Dumpster rental' },
  { key: 'parking', label: 'Parking' },
];

export const CLEAN_PIT_LABEL = 'Clean pit of oil & debris';

/** One pit per hoistway, every room. The ceiling on "how many are dirty". */
export function pitCount(doc: SurveyDocument): number {
  return doc.rooms.reduce((total, room) => total + room.hoistways.length, 0);
}

/* ─────────────────────────────── mutators ───────────────────────────────── */

export function setAdHocFlag(
  document: SurveyDocument,
  key: AdHocFlagKey,
  value: boolean,
): void {
  document.adHoc[key] = value;
}

/**
 * Toggling the dirty-pit row off ZEROES its count rather than hiding a stale
 * one — the same rule the catalog's flagCount controls follow. A remembered "2"
 * behind an unticked row is a line item nobody agreed to.
 */
export function setCleanPits(document: SurveyDocument, flag: boolean, count: number): void {
  document.adHoc.cleanPitOfOilAndDebris = flag
    ? { flag: true, count: Math.max(0, Math.trunc(count)) }
    : { flag: false, count: 0 };
}

/**
 * The "How many pits are dirty?" stepper's write — a COUNT, never the flag
 * (D-055). The stepper only exists while the row is ticked, but its write can
 * land after the row was unticked: on iOS a typed count is committed when the
 * stepper unmounts (no blur — D-053), and unticking is what unmounts it. Read
 * against the document the write lands in, an unticked row stays unticked and
 * the count is dropped; a ticked row (the surveyor left the screen with the
 * keyboard up) takes the count, as D-053 intends. Returns whether it wrote.
 */
export function setDirtyPitCount(document: SurveyDocument, count: number): boolean {
  if (!document.adHoc.cleanPitOfOilAndDebris.flag) return false;
  setCleanPits(document, true, count);
  return true;
}

/**
 * The synthetic question id a free-form item's photos were tagged with.
 *
 * History, not a door (task 7, controller ruling 7): the phone no longer
 * writes `AdHocItems.other` — the "Something else" button, its cards and
 * their photo picker are gone (the design authority named the picker
 * leftover code, inconsistent with the carousel every other screen shows,
 * and asked for a single "Notes for this Section" field instead of
 * open-ended items). This function survives only so `adhoc.other.<id>`, the
 * id grammar an OLD photo can still carry, keeps parsing —
 * `photo-device-path.test.ts` and `api.test.ts` pin exactly that.
 */
export function adHocPhotoQuestionId(id: string): string {
  return `adhoc.other.${id}`;
}
